/**
 * PermissionProvider + useCan (PERM-01) — the client side of the RBAC framework.
 *
 * Loads the signed-in user's role (+ per-user grant/deny overrides) once, resolves
 * their effective capability set, and exposes `can(key)` to the whole app. Admin
 * is a wildcard: `can()` is always true for an admin, including for capabilities
 * added in the future. Gate any action with `useCan("module.action")`.
 *
 * Enforcement note: this is the CLIENT gate (hide/disable UI). The server-side
 * gate is `pegasus_can()` in Postgres for RLS on data tables (PERM-04, applied as
 * we tighten each table from "any authenticated" to "authenticated AND capable").
 * Until then we FAIL OPEN (below) so the framework can be wired everywhere without
 * risking locking the single admin user out mid-build.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";

/**
 * During the framework's infancy (one admin user, no per-table RLS yet) we allow
 * when permissions are unknown — so wiring `useCan` everywhere can't accidentally
 * hide things from the owner. Flip to `false` to ENFORCE once roles are populated
 * for real users and the RLS layer (PERM-04) is in. */
const FAIL_OPEN = true;

interface PermState {
  ready: boolean;
  isAdmin: boolean;
  roleKey: string | null;
  caps: Set<string>;
}

interface PermissionValue {
  can: (key: string) => boolean;
  isAdmin: boolean;
  ready: boolean;
  roleKey: string | null;
}

const PermissionContext = createContext<PermissionValue | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<PermState>({
    ready: false,
    isAdmin: false,
    roleKey: null,
    caps: new Set(),
  });

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setState({ ready: true, isAdmin: false, roleKey: null, caps: new Set() });
      return;
    }
    (async () => {
      const { data: u } = await supabase
        .from("app_user")
        .select("role_key, grant_extra, deny")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!u) {
        // User not seeded yet — unknown. (FAIL_OPEN governs can().)
        setState({ ready: true, isAdmin: false, roleKey: null, caps: new Set() });
        return;
      }
      const { data: r } = await supabase
        .from("app_role")
        .select("is_admin, capabilities")
        .eq("key", (u as { role_key: string }).role_key)
        .maybeSingle();
      if (cancelled) return;
      const isAdmin = Boolean(r?.is_admin);
      const caps = new Set<string>([
        ...(((r?.capabilities as string[]) ?? []) || []),
        ...(((u as { grant_extra?: string[] }).grant_extra ?? []) || []),
      ]);
      for (const d of (u as { deny?: string[] }).deny ?? []) caps.delete(d);
      setState({
        ready: true,
        isAdmin,
        roleKey: (u as { role_key: string }).role_key,
        caps,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const can = useCallback(
    (key: string) => {
      if (state.isAdmin) return true; // wildcard — incl. future capabilities
      if (!state.ready || !state.roleKey) return FAIL_OPEN; // unknown → see flag
      return state.caps.has(key);
    },
    [state],
  );

  return (
    <PermissionContext.Provider
      value={{ can, isAdmin: state.isAdmin, ready: state.ready, roleKey: state.roleKey }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionValue {
  const ctx = useContext(PermissionContext);
  if (!ctx)
    throw new Error("usePermissions must be used within a PermissionProvider");
  return ctx;
}

/** Gate any action: `const canReassign = useCan("dispatch.reassign");` */
export function useCan(key: string): boolean {
  return usePermissions().can(key);
}
